import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../contexts/APIContext';
import { showToast } from '../components/Toast';
import FunnelWaitVideo from '../components/FunnelWaitVideo';
import FunnelWelcomePage from './FunnelWelcomePage';
import {
  FunnelScreenStyles,
  FunnelSky,
  FunnelNameStep,
  FunnelFriendStep,
  FunnelMeetStep,
  FunnelSigninStep,
  CheckIcon,
  sheetStepVariants,
  LoadingPandaVideo,
} from '../components/funnel/FunnelScreens';
import {
  FunnelExplainerStyles,
  ExplainerSlide,
  EXPLAINER_BY_KEY,
  EXPLAINER_STEPS,
  EXPLAINER_PRELOAD_AHEAD,
} from '../components/funnel/FunnelExplainer';
import { useFunnelAnalytics, FUNNEL_STARTED_KEY } from '../hooks/useFunnelAnalytics';
import { EVENTS } from '../constants/events';

// sessionStorage keys. The funnel's redirect_uri/state are NOT stored — they
// ride in the URL through the whole flow (incl. the Google round trip). Only
// the funnel's own answers persist here, so they can be saved to the account
// after sign-in.
const FUNNEL_STEP_KEY = 'rc_funnel_step';
const FUNNEL_NAME_KEY = 'rc_funnel_child_name';
const FUNNEL_PREVIEW_KEY = 'rc_funnel_preview_img';
// Written by AuthContext during a funnel sign-in only: whether the users row
// existed before that sign-in auto-created it (from the backend exchange)
const SIGNIN_USER_EXISTED_KEY = 'pixley_signin_user_existed';
// '1' once the visitor chose "See how Pixley works" on the welcome screen, which
// puts the explainer slides ahead of picking a friend (and in the progress bar). Kept so
// a reload mid-explainer resumes on the same path.
const FUNNEL_EXPLAINER_KEY = 'rc_funnel_explainer';

const FUNNEL_KEYS = [
  FUNNEL_STEP_KEY, FUNNEL_NAME_KEY,
  FUNNEL_PREVIEW_KEY, SIGNIN_USER_EXISTED_KEY,
  FUNNEL_EXPLAINER_KEY,
  // Retired questions (age, screen-time struggle) and the retired free-book
  // funnel's answers — still cleared so a session that started on an old
  // bundle doesn't leave them behind in the tab.
  'rc_funnel_child_age', 'rc_funnel_screen_concern',
  'rc_funnel_book_key', 'rc_funnel_book_prompt', 'rc_funnel_book_started',
  // Cleared too, so a second funnel run in the same tab counts as a new start
  FUNNEL_STARTED_KEY,
];
export const clearFunnelSession = () =>
  FUNNEL_KEYS.forEach((k) => sessionStorage.removeItem(k));

// iOS onboarding theme (NewOnboardingKit.swift)
const ACCENT = '#0A6CFF';
const CONFETTI_COLORS = [ACCENT, '#FFC83D', '#7C5CFF', '#34C759', '#FF6FA5'];

// Ready-made friends for visitors with no photo handy. `key` matches the
// backend's DEFAULT_AVATAR_KEYS (Pixley-Backend/routers/profiles_router.py) —
// the same starter characters new profiles get auto-assigned — so the funnel and
// the app offer one set, not two that drift apart.
//
// Served from /assets/funnel-friends/<key>.webp: 768px, 17-29KB each generated
// from the 1024px PNGs in /assets. 768 is exactly the cap blobToDataURL applies
// to a generated preview, so picking a friend and generating from a photo
// produce the same-sized character image downstream.
//
// `kind` is not rendered — it exists only for the aria-label, so a screen-reader
// user hears "Use Willow the deer" instead of a bare name for art they can't see.
const PRESET_FRIENDS = [
  { key: 'benny', name: 'Benny', kind: 'bunny' },
  { key: 'zippy-icon', name: 'Zippy', kind: 'tiger' },
  { key: 'willow', name: 'Willow', kind: 'deer' },
];

// ── flow ───────────────────────────────────────────────────────────────────
// The funnel can open with the explainer slides (FunnelExplainer), for visitors
// who tap "See how Pixley works" on the welcome screen. BASE_STEP_ORDER is the
// flow without them; the page prepends EXPLAINER_STEPS when they were chosen.
// Naming comes after the character reveal, right before sign-in: the visitor
// sees what they're getting before being asked for anything.
const BASE_STEP_ORDER = ['create', 'meet', 'name', 'signin'];
// Full flow order (incl. transient steps) — used to pick the slide direction
const FLOW_ORDER = [...EXPLAINER_STEPS, 'create', 'generating', 'meet', 'name', 'signin', 'finishing'];
// Where the header back button leads from each step
const BACK_TARGET = {
  episode: 'friend', personal: 'episode',
  meet: 'create', name: 'meet', signin: 'name',
};

// The funnel is a sign-up flow: accounts that existed before this sign-in
// get welcomed back and sent to the main site instead.
//
// The authoritative answer comes from the backend: the OAuth exchange checks
// for the users row BEFORE auto-creating it and returns user_already_existed,
// which AuthContext stashes under SIGNIN_USER_EXISTED_KEY (funnel sign-ins
// only). The heuristic below only covers sessions where no exchange happened
// in this tab (e.g. the user was already signed in when they opened the
// funnel link — which itself means the account predates the funnel visit).
const ACCOUNT_AGE_NEW_MS = 15 * 60 * 1000;
// Exported so the second funnel (FunnelIntroAuthPage) makes the same call from
// the same signals — "is this a returning account?" decides who gets sent home
// instead of on to checkout, and two copies of it would drift.
export const isExistingAccount = (user) => {
  if (!user) return false;
  const existedFlag = sessionStorage.getItem(SIGNIN_USER_EXISTED_KEY);
  if (existedFlag === '1') return true;
  if (existedFlag === '0') return false;
  // Fallback inference: onboarded persona, any characters, or a row older
  // than this sign-in (fresh accounts are rows created seconds ago)
  let persona = user.persona;
  if (typeof persona === 'string') {
    try {
      persona = JSON.parse(persona);
    } catch {
      persona = null;
    }
  }
  if (persona && typeof persona === 'object' && !Array.isArray(persona) && Object.keys(persona).length > 0) return true;
  if (Array.isArray(user.characters) && user.characters.length > 0) return true;
  const joined = Date.parse(user.join_date || user.created_at || '');
  if (!Number.isNaN(joined) && Date.now() - joined > ACCOUNT_AGE_NEW_MS) return true;
  return false;
};


// Rotating status lines while the character generates
const GEN_MESSAGES = [
  'Pixley is sketching the outlines…',
  'Mixing the perfect colors…',
  'Adding a spark of magic…',
  'Putting on the finishing touches…',
];

// ── motion variants (step slide + staggered content reveal) ────────────────
const stepVariants = {
  enter: (dir) => ({ opacity: 0, x: dir >= 0 ? 56 : -56, scale: 0.98 }),
  center: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.06,
      delayChildren: 0.04,
    },
  },
  exit: (dir) => ({
    opacity: 0,
    x: dir >= 0 ? -56 : 56,
    scale: 0.98,
    transition: { duration: 0.22, ease: 'easeIn' },
  }),
};

const itemVariants = {
  enter: { opacity: 0, y: 18 },
  center: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// ── image helpers ──────────────────────────────────────────────────────────

// Downscale a Blob to a JPEG data URL small enough for sessionStorage
const blobToDataURL = (blob, maxDim = 768) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });

const dataURLtoBlob = (dataUrl) => {
  const [meta, b64] = dataUrl.split(',');
  const mime = meta.match(/data:(.*?);/)?.[1] || 'image/jpeg';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

// ── meet-step confetti (from NOMeetPixleyStep) ─────────────────────────────

const Confetti = () => {
  const pieces = useRef(
    Array.from({ length: 45 }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      dur: 2.2 + Math.random() * 1.6,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      drift: (Math.random() - 0.5) * 160,
      spin: 360 + Math.random() * 360,
    }))
  ).current;
  return (
    <div className="fa-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
            '--drift': `${p.drift}px`,
            '--spin': `${p.spin}deg`,
          }}
        />
      ))}
    </div>
  );
};

/**
 * Authentication step for RevenueCat Web Funnels, rebuilt as the iOS
 * onboarding (new_onboarding/): pick a friend or add a photo → watch Pixley
 * bring them to life → name your hero → sign in to save them → redirect back
 * to the funnel with `state` + `app_user_id`.
 * https://www.revenuecat.com/docs/tools/funnels/creating-funnels#authentication-steps
 */
const FunnelAuthPage = () => {
  // Whether this visitor is taking the explainer path (see FUNNEL_EXPLAINER_KEY)
  const [explained, setExplained] = useState(() => sessionStorage.getItem(FUNNEL_EXPLAINER_KEY) === '1');
  const STEP_ORDER = explained ? [...EXPLAINER_STEPS, ...BASE_STEP_ORDER] : BASE_STEP_ORDER;

  const [searchParams] = useSearchParams();
  const { signInWithGoogle, isAuthenticated, loading, user } = useAuth();
  const { apiClient } = useAPI();
  const hasCompletedRef = useRef(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const [step, setStepState] = useState(() => {
    const saved = sessionStorage.getItem(FUNNEL_STEP_KEY);
    // 'generating' can't be resumed after a reload — restart from create
    if (saved === 'generating') return 'create';
    // Naming comes after the character now. A session saved on the name step
    // with no character (it started when naming came first) makes one first.
    if (saved === 'name' && !sessionStorage.getItem(FUNNEL_PREVIEW_KEY)) return 'create';
    return saved && STEP_ORDER.includes(saved) ? saved : 'create';
  });
  const [childName, setChildName] = useState(() => sessionStorage.getItem(FUNNEL_NAME_KEY) || '');
  const [previewImage, setPreviewImage] = useState(() => sessionStorage.getItem(FUNNEL_PREVIEW_KEY) || null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  // Key of the ready-made friend currently being loaded (null = none)
  const [loadingFriend, setLoadingFriend] = useState(null);
  // What's selected on the create step: { type: 'friend', key } or
  // { type: 'photo' }. Nothing starts selected: the button waits for a choice.
  const [pick, setPick] = useState(null);
  // The ready-made friend on the meet and sign-in steps, introduced by name.
  // Null for a photo, and after a reload, when those steps use wording that
  // fits either.
  const [metFriend, setMetFriend] = useState(null);
  const [genProgress, setGenProgress] = useState(0);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [heroRevealed, setHeroRevealed] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [genMsgIndex, setGenMsgIndex] = useState(0);
  const [showExistingAccount, setShowExistingAccount] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => !sessionStorage.getItem(FUNNEL_STEP_KEY));

  const funnel = useFunnelAnalytics();

  // Slide direction: moving forward slides left, going back slides right
  const prevStepRef = useRef(step);
  const direction = FLOW_ORDER.indexOf(step) >= FLOW_ORDER.indexOf(prevStepRef.current) ? 1 : -1;
  useEffect(() => {
    prevStepRef.current = step;
  }, [step]);

  const setStep = useCallback((next) => {
    sessionStorage.setItem(FUNNEL_STEP_KEY, next);
    setStepState(next);
    window.scrollTo({ top: 0 });
  }, []);

  // Warm the next explainer slide's art while this one is up
  const preloadedRef = useRef(new Set());
  useEffect(() => {
    (EXPLAINER_PRELOAD_AHEAD[step] || []).forEach((src) => {
      if (preloadedRef.current.has(src)) return;
      preloadedRef.current.add(src);
      new Image().src = src;
    });
  }, [step]);

  // Existing-account modal lingers a moment before leaving for the main site
  const redirectTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(redirectTimerRef.current), []);

  // Buffer the generating-step panda video while they pick a photo —
  // otherwise it only starts downloading when the wait screen mounts, which
  // on a slow connection means a blank circle at the funnel's most anxious
  // moment. The detached element is held in a ref so buffering isn't GC'd;
  // the wait screen's <video> then plays from the media cache instantly.
  const prefetchVideoRef = useRef(null);
  useEffect(() => {
    // `step` is already 'create' behind the welcome screen; don't compete with
    // its art for bandwidth.
    if (showWelcome || step !== 'create' || prefetchVideoRef.current) return;
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'auto';
    [
      { src: '/assets/loading-panda.webm', type: 'video/webm' },
      { src: '/assets/loading-panda.mp4', type: 'video/mp4' },
    ].forEach(({ src, type }) => {
      const source = document.createElement('source');
      source.src = src;
      source.type = type;
      video.appendChild(source);
    });
    video.load();
    prefetchVideoRef.current = video;
  }, [step, showWelcome]);

  // Warm the API connection (DNS + TCP + TLS) the moment a photo is chosen —
  // generation is one tap away, and a real request beats a preconnect hint
  // that would have idled out minutes before the user got here.
  const apiWarmedRef = useRef(false);
  useEffect(() => {
    if (!photoFile || apiWarmedRef.current || !apiClient) return;
    apiWarmedRef.current = true;
    apiClient.healthCheck().catch(() => {});
  }, [photoFile, apiClient]);

  // The name is never sent to analytics — only its length, so a truncated or
  // placeholder entry is still visible in the drop-off data.
  const submitName = (method) => {
    funnel.trackStepCompleted('name', {
      method,
      name_length: childName.trim().length,
    });
    setStep('signin');
  };

  // Funnel params. On first arrival RevenueCat passes `redirect_uri`/`state`;
  // after the Google round trip we land back here with the same values under
  // `funnel_redirect_uri`/`funnel_state` (prefixed so they don't collide with
  // the OAuth `state`/`code` Supabase appends). These stay in the URL the whole
  // time, so they're never cached — only the funnel's own answers (name,
  // character preview) live in sessionStorage.
  const redirectUri =
    searchParams.get('redirect_uri') || searchParams.get('funnel_redirect_uri');
  const funnelState =
    searchParams.get('state') || searchParams.get('funnel_state');

  // ── analytics: one step_viewed + virtual pageview per view ────────────────
  // Every step of this funnel lives at /funnel-auth, so nothing distinguishes
  // them in PostHog unless each one announces itself here.
  useEffect(() => {
    if (!redirectUri || !funnelState) {
      funnel.trackInvalidLink();
      return;
    }
    funnel.trackView(showWelcome ? 'welcome' : step);
  }, [showWelcome, step, redirectUri, funnelState, funnel]);

  // ── completion: save the held character, then bounce back to the funnel ──
  const completeAndRedirect = useCallback(async () => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;

    // Hand the authenticated user back to the RevenueCat funnel (→ checkout /
    // next step) carrying the unchanged state + their app_user_id.
    const returnToFunnel = async ({ isExisting }) => {
      try {
        const result = await apiClient.completeFunnelAuth(redirectUri, funnelState);
        if (result.status && result.data?.redirect_url) {
          logger.log('✅ Funnel auth complete, returning to funnel');
          // Fire before clearFunnelSession() — it wipes the answers this event
          // reports — and send it unbatched, since the redirect below can
          // outrun PostHog's next flush.
          funnel.trackImmediate(EVENTS.FUNNEL_COMPLETED, {
            outcome: 'funnel_checkout',
            is_existing_account: isExisting,
            has_character: !!sessionStorage.getItem(FUNNEL_PREVIEW_KEY),
          });
          clearFunnelSession();
          setShowSuccess(true);
          window.location.replace(result.data.redirect_url);
        } else {
          throw new Error(result.error || 'Failed to complete funnel authentication');
        }
      } catch (error) {
        logger.error('❌ Funnel auth completion error:', error);
        funnel.track(EVENTS.FUNNEL_COMPLETION_FAILED, {
          is_existing_account: isExisting,
          error_message: error.message || 'unknown',
          status_code: error.status || null,
        });
        hasCompletedRef.current = false;
        setIsSigningIn(false);
        setStep('signin');
        showToast(error.message || 'Something went wrong. Please try again.', 'error');
      }
    };

    // The funnel is for NEW sign-ups. An existing account keeps all its data
    // untouched (no character/persona writes). Where it goes next depends on
    // whether it already has an active RevenueCat subscription: only paying
    // subscribers skip the funnel and get welcomed home — everyone else is
    // sent on to the funnel checkout to (re)subscribe.
    if (isExistingAccount(user)) {
      let isSubscribed = false;
      try {
        // force=true busts the backend resolve cache for a fresh RevenueCat
        // read (they may have subscribed/lapsed since it was last cached).
        const sub = await apiClient.getSubscriptionStatus(user.id, { force: true });
        const tier = sub?.data?.subscription;
        isSubscribed = Boolean(sub?.data?.premium) || (!!tier && tier !== 'NONE');
      } catch (error) {
        // On an RC/backend hiccup, don't shortcut them home on an unverified
        // subscription — let them continue through the funnel checkout.
        logger.warn('Funnel: subscription check failed for returning account:', error);
      }

      funnel.track(EVENTS.FUNNEL_EXISTING_ACCOUNT, { is_subscribed: isSubscribed });

      if (isSubscribed) {
        logger.log('👋 Existing subscriber — skipping funnel, redirecting home');
        funnel.trackImmediate(EVENTS.FUNNEL_COMPLETED, {
          outcome: 'existing_subscriber_home',
          is_existing_account: true,
          has_character: !!sessionStorage.getItem(FUNNEL_PREVIEW_KEY),
        });
        clearFunnelSession();
        setShowExistingAccount(true);
        redirectTimerRef.current = setTimeout(() => {
          window.location.replace('https://www.pixleyai.com');
        }, 3400);
        return;
      }

      logger.log('↩️ Existing account without an active subscription — continuing to funnel checkout');
      setStep('finishing');
      await returnToFunnel({ isExisting: true });
      return;
    }

    setStep('finishing');

    // Best-effort: keep the character they just made (mirrors the iOS flow,
    // which uploads the held preview after sign-in). Never blocks the funnel.
    try {
      const previewDataUrl = sessionStorage.getItem(FUNNEL_PREVIEW_KEY);
      const savedName = sessionStorage.getItem(FUNNEL_NAME_KEY);
      if (previewDataUrl && savedName && user?.id) {
        await apiClient.saveFunnelCharacter({
          userId: user.id,
          name: savedName,
          animatedImage: dataURLtoBlob(previewDataUrl),
        });
        logger.log('✅ Funnel character saved to account');
      }
    } catch (error) {
      logger.warn('Funnel character save failed (non-blocking):', error);
    }

    // Best-effort: seed the account persona with what the funnel learned
    // (the child's name) in the same shape OnboardingContext saves, so
    // stories personalize from day one — and since a non-empty persona marks
    // onboarding as done, funnel users aren't re-onboarded in-app. Existing
    // personas are merged, not replaced.
    try {
      const personaName = sessionStorage.getItem(FUNNEL_NAME_KEY);
      if (personaName && user?.id) {
        let existing = user.persona;
        if (typeof existing === 'string') {
          try {
            existing = JSON.parse(existing);
          } catch {
            existing = null;
          }
        }
        if (!existing || typeof existing !== 'object' || Array.isArray(existing)) existing = {};

        const persona = {
          favorite_shows: [],
          aggregated_topics: [],
          aggregated_interests: [],
          aggregated_preferences: [],
          profile_picture: null,
          discovery_source: [],
          ...existing,
          child_name: existing.child_name || personaName || null,
          // The funnel no longer asks for an age, but the key is still written
          // so an existing value survives the merge rather than being dropped
          // from the stored persona.
          child_age: existing.child_age || null,
          onboarding_source: existing.onboarding_source || 'web_funnel',
        };

        await apiClient.updateUserPersona(user.id, persona);
        logger.log('✅ Funnel persona saved to account');
      }
    } catch (error) {
      logger.warn('Funnel persona save failed (non-blocking):', error);
    }

    // Stamp the person so funnel sign-ups stay identifiable in cohorts long
    // after the funnel events themselves scroll out of the retention window.
    funnel.setPersonProperties({
      signup_source: 'web_funnel',
      funnel_name: 'web2app',
    });

    await returnToFunnel({ isExisting: false });
  }, [apiClient, redirectUri, funnelState, user, setStep, funnel]);

  // Fires when the user lands back authenticated after the OAuth round trip
  // (step === 'signin'), or arrives already signed in before starting
  // (step === 'create' untouched) — either way, finish and return to the funnel.
  useEffect(() => {
    if (loading || !isAuthenticated || !apiClient) return;
    if (hasCompletedRef.current) return;
    if (!redirectUri || !funnelState) {
      logger.warn('Funnel auth: missing redirect_uri or state');
      return;
    }
    // Funnel data is waiting to be saved (character and/or persona) — hold
    // until the user object (and its id) has loaded so the saves aren't
    // silently skipped
    const hasFunnelData =
      sessionStorage.getItem(FUNNEL_PREVIEW_KEY) ||
      sessionStorage.getItem(FUNNEL_NAME_KEY);
    if (hasFunnelData && !user?.id) return;
    if (step === 'signin' || step === 'create') {
      completeAndRedirect();
    }
  }, [isAuthenticated, loading, apiClient, redirectUri, funnelState, step, user, completeAndRedirect]);

  // ── generation (same /previewCharacterImage the iOS onboarding calls) ────
  useEffect(() => {
    if (step !== 'generating') return undefined;
    // Ease toward 95%, slowing as it approaches — keeps the bar alive during
    // an open-ended wait without ever finishing early (NewOnboardingFlow)
    const id = setInterval(() => {
      setGenProgress((p) => (p >= 0.95 ? p : p + (0.95 - p) * 0.08));
    }, 500);
    return () => clearInterval(id);
  }, [step]);

  // Cycle the "Pixley is painting" status lines while generating
  useEffect(() => {
    if (step !== 'generating') return undefined;
    setGenMsgIndex(0);
    const id = setInterval(() => setGenMsgIndex((i) => (i + 1) % GEN_MESSAGES.length), 2600);
    return () => clearInterval(id);
  }, [step]);

  const startGeneration = async (image) => {
    setGenProgress(0);
    setStep('generating');

    const startedAt = Date.now();
    // Counts the /previewCharacterImage attempts this run actually spent, so a
    // slow-but-successful retry is distinguishable from a first-try success.
    let attempts = 0;
    funnel.track(EVENTS.FUNNEL_GENERATION_STARTED, {
      photo_size_kb: image?.size ? Math.round(image.size / 1024) : null,
    });

    try {
      let tokenResp;
      try {
        tokenResp = await apiClient.getFunnelPreviewToken(redirectUri, funnelState);
      } catch (error) {
        if (error.status === 429) {
          // Minting is IP rate-limited as a flood backstop; the generation
          // budget itself is charged later, on /previewCharacterImage.
          throw new Error("You've hit the character-preview limit for now. Please wait a bit and try again.");
        }
        throw error;
      }
      const previewToken = tokenResp.token || tokenResp.data?.token;
      if (!previewToken) throw new Error('Could not start character generation. Please try again.');

      // 2 auto-attempts max. Each one charges the per-IP budget
      // (WEB_PREVIEW_IP_MAX_PER_HOUR, 8/hour) as well as the per-session one,
      // so keep this small — it must leave room for manual "try a different
      // photo" do-overs before the visitor hits a 429.
      let lastError;
      for (let attempt = 0; attempt < 2; attempt++) {
        attempts += 1;
        try {
          const blob = await apiClient.previewCharacterImageWeb({
            image,
            handdrawn: false,
            previewToken,
          });
          const dataUrl = await blobToDataURL(blob, 768);
          sessionStorage.setItem(FUNNEL_PREVIEW_KEY, dataUrl);
          setPreviewImage(dataUrl);
          setGenProgress(1);
          setHeroRevealed(false);
          funnel.track(EVENTS.FUNNEL_GENERATION_COMPLETED, {
            duration_seconds: Math.round((Date.now() - startedAt) / 1000),
            attempts,
          });
          funnel.trackStepCompleted('create', { creation_method: 'photo' });
          setTimeout(() => setStep('meet'), 400);
          return;
        } catch (error) {
          lastError = error;
          // Only retry faults that might be transient. A 4xx is deterministic for
          // this input — safety block, blocked name, oversize photo, or a spent
          // preview budget (429) — so retrying returns the same answer AND spends
          // another slot from the per-IP budget. 5xx can be a blip, so retry those.
          if (error.status >= 400 && error.status < 500) break;
          if (/inappropriate|safety|copyright/i.test(error.message || '')) break;
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
      throw lastError || new Error('Character generation failed');
    } catch (error) {
      logger.error('❌ Funnel preview generation failed:', error);
      funnel.track(EVENTS.FUNNEL_GENERATION_FAILED, {
        duration_seconds: Math.round((Date.now() - startedAt) / 1000),
        attempts,
        error_message: error.message || 'unknown',
        status_code: error.status || null,
        // Safety blocks and rate limits are the two failures worth splitting
        // out: one is content, the other is capacity.
        is_rate_limited: error.status === 429,
        is_safety_block: /inappropriate|safety|copyright/i.test(error.message || ''),
      });
      showToast(error.message || "We couldn't bring your character to life. Please try again.", 'error');
      setStep('create');
    }
  };

  const handleGenerate = () => {
    if (!photoFile) {
      showToast('Add a photo first!', 'warning');
      return;
    }
    startGeneration(photoFile);
  };

  // Picking a ready-made friend skips image generation entirely: no
  // /previewCharacterImage call, so it costs nothing, never charges the per-IP
  // preview budget, and can't fail on a safety block. The chosen art is written
  // into FUNNEL_PREVIEW_KEY in exactly the shape a generated preview takes
  // (768px JPEG data URL), so the meet step, sign-in and saveFunnelCharacter
  // downstream need no special-casing — the character is still saved under the
  // child's name, the friend is just the artwork standing in for a photo.
  const choosePresetFriend = async (friend) => {
    if (loadingFriend) return;
    setLoadingFriend(friend.key);
    try {
      const response = await fetch(`/assets/funnel-friends/${friend.key}.webp`);
      if (!response.ok) throw new Error(`Could not load ${friend.name}`);
      const dataUrl = await blobToDataURL(await response.blob(), 768);
      sessionStorage.setItem(FUNNEL_PREVIEW_KEY, dataUrl);
      setPreviewImage(dataUrl);
      funnel.track(EVENTS.FUNNEL_PRESET_FRIEND_SELECTED, {
        friend: friend.key,
        friend_name: friend.name,
        success: true,
        // Whether the escape hatch is used instead of a photo, or after one
        // was already added, says different things about the photo ask.
        had_photo: !!photoFile,
      });
      funnel.trackStepCompleted('create', { creation_method: 'preset_friend' });
      // No progress bar to run down — straight to the reveal
      setHeroRevealed(false);
      setStep('meet');
    } catch (error) {
      logger.error('Preset friend selection failed:', error);
      funnel.track(EVENTS.FUNNEL_PRESET_FRIEND_SELECTED, {
        friend: friend.key,
        success: false,
        error_message: error.message || 'unknown',
      });
      showToast("We couldn't load that friend. Please try another.", 'error');
    } finally {
      setLoadingFriend(null);
    }
  };

  // The create step's button: meet the selected friend, or generate from the
  // photo. Selecting on the step itself only highlights a choice.
  const confirmPick = () => {
    if (!pick) return;
    if (pick.type === 'friend') {
      const friend = PRESET_FRIENDS.find((f) => f.key === pick.key);
      if (!friend) return;
      setMetFriend(friend);
      choosePresetFriend(friend);
    } else {
      setMetFriend(null);
      handleGenerate();
    }
  };

  // A newly added photo (gallery, native camera or the in-page one) becomes
  // the selection, replacing whichever friend was highlighted
  useEffect(() => {
    if (photoPreviewUrl) setPick({ type: 'photo' });
  }, [photoPreviewUrl]);

  // `source` distinguishes the two hidden inputs ('upload' = gallery/file
  // picker, 'camera_native' = the mobile capture input) for analytics; the
  // desktop in-page camera reports itself from capturePhoto().
  const handlePhotoChange = (e, source = 'upload') => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(URL.createObjectURL(file));
    funnel.track(EVENTS.FUNNEL_PHOTO_ADDED, {
      method: source,
      // A replaced photo usually means the first attempt was rejected or
      // disliked — worth separating from a first add.
      is_replacement: !!photoFile,
      photo_size_kb: file.size ? Math.round(file.size / 1024) : null,
      photo_type: file.type || null,
    });
  };

  // ── "Take photo" ──────────────────────────────────────────────────────────
  // The input's `capture` attribute only opens a camera on phones/tablets —
  // desktop browsers silently ignore it and show the file picker. On desktop
  // we open an in-page getUserMedia camera instead.
  const isTouchDevice =
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent)); // iPadOS reports as Mac

  const openCamera = async () => {
    if (isTouchDevice || !navigator.mediaDevices?.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      cameraStreamRef.current = stream;
      setCameraOpen(true);
    } catch (error) {
      logger.warn('Camera unavailable, falling back to file picker:', error);
      showToast("We couldn't open your camera — choose a photo instead.", 'warning');
      fileInputRef.current?.click();
    }
  };

  const closeCamera = useCallback(() => {
    cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
    cameraStreamRef.current = null;
    setCameraOpen(false);
  }, []);

  // Attach the stream once the modal's <video> is in the DOM
  useEffect(() => {
    if (cameraOpen && cameraVideoRef.current && cameraStreamRef.current) {
      cameraVideoRef.current.srcObject = cameraStreamRef.current;
    }
  }, [cameraOpen]);

  // Never leave the camera running on unmount
  useEffect(() => closeCamera, [closeCamera]);

  const capturePhoto = () => {
    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
        setPhotoFile(file);
        setPhotoPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(file);
        });
        funnel.track(EVENTS.FUNNEL_PHOTO_ADDED, {
          method: 'camera_web',
          is_replacement: !!photoFile,
          photo_size_kb: file.size ? Math.round(file.size / 1024) : null,
          photo_type: file.type || null,
        });
        closeCamera();
      },
      'image/jpeg',
      0.92
    );
  };

  // Hero pop-in on the meet step (spring-ish scale, then confetti)
  useEffect(() => {
    if (step !== 'meet') return undefined;
    const id = setTimeout(() => setHeroRevealed(true), 120);
    return () => clearTimeout(id);
  }, [step]);

  const handleGoogleSignIn = async () => {
    try {
      logger.log('Starting Google OAuth (funnel)...');
      setIsSigningIn(true);
      // Fired before the redirect: this is the last event PostHog gets from
      // this page load, so signin_started vs. the post-OAuth 'finishing' view
      // is what measures Google round-trip abandonment.
      funnel.trackStepCompleted('signin', { method: 'google' }, { immediate: true });
      funnel.trackImmediate(EVENTS.FUNNEL_SIGNIN_STARTED, { method: 'google' });

      // redirect_uri/state ride back in the return URL below — no need to
      // stash them. Only the funnel's own answers stay in sessionStorage so
      // they can be saved to the account after the Google round trip.
      sessionStorage.setItem(FUNNEL_STEP_KEY, 'signin');
      if (childName) sessionStorage.setItem(FUNNEL_NAME_KEY, childName);

      // Land straight back on /funnel-auth (not the site root) after Google,
      // carrying the funnel params in the URL so the flow resumes without
      // depending on any storage. Prefixed names avoid colliding with the
      // OAuth `state`/`code` that Supabase appends to this URL on return.
      const returnUrl = new URL('/funnel-auth', window.location.origin);
      if (redirectUri) returnUrl.searchParams.set('funnel_redirect_uri', redirectUri);
      if (funnelState) returnUrl.searchParams.set('funnel_state', funnelState);

      const timeoutId = setTimeout(() => {
        setIsSigningIn(false);
        funnel.track(EVENTS.FUNNEL_SIGNIN_FAILED, { method: 'google', reason: 'timeout' });
        showToast('Sign in is taking too long. Please try again.', 'error');
      }, 15000);

      const result = await signInWithGoogle(returnUrl.toString());
      clearTimeout(timeoutId);

      if (result && result.success) {
        logger.log('Google OAuth initiated successfully');
      } else {
        setIsSigningIn(false);
        funnel.track(EVENTS.FUNNEL_SIGNIN_FAILED, {
          method: 'google',
          reason: 'oauth_init_failed',
          error_message: result?.error || 'unknown',
        });
        showToast(result?.error || 'Sign in failed. Please try again.', 'error');
      }
    } catch (error) {
      logger.error('Google sign in error:', error);
      setIsSigningIn(false);
      funnel.track(EVENTS.FUNNEL_SIGNIN_FAILED, {
        method: 'google',
        reason: 'exception',
        error_message: error.message || 'unknown',
      });
      showToast(error.message || 'Failed to sign in with Google', 'error');
    }
  };

  const stepIndex = STEP_ORDER.indexOf(step === 'generating' ? 'create' : step === 'finishing' ? 'signin' : step);
  // Picking a friend is the first step, with nothing behind it — unless the
  // visitor came through the explainer, whose last slide is then one step back
  const backTarget = step === 'create' && explained
    ? EXPLAINER_STEPS[EXPLAINER_STEPS.length - 1]
    : BACK_TARGET[step];

  const invalidLink = !redirectUri || !funnelState;

  // ── render ────────────────────────────────────────────────────────────────
  const cardKey = invalidLink ? 'invalid' : step;

  if (showWelcome && !invalidLink) {
    return (
      <FunnelWelcomePage
        onContinue={() => {
          funnel.trackStepCompleted('welcome');
          sessionStorage.removeItem(FUNNEL_EXPLAINER_KEY);
          setExplained(false);
          setStep('create');
          setShowWelcome(false);
        }}
        onExplore={() => {
          funnel.trackStepCompleted('welcome');
          sessionStorage.setItem(FUNNEL_EXPLAINER_KEY, '1');
          setExplained(true);
          setStep(EXPLAINER_STEPS[0]);
          setShowWelcome(false);
        }}
      />
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <FunnelAuthPageStyles />
      <FunnelScreenStyles />
      <FunnelExplainerStyles />
      <div className={`fa-bg${cardKey === 'signin' ? ' is-sheet' : ''}`}>
        {/* Sign-in rises into the moon's sky; it's fetched a step ahead */}
        <FunnelSky
          scene={cardKey === 'signin' || cardKey === 'finishing' ? 'moon' : 'night'}
          moonSoon={cardKey === 'meet' || cardKey === 'name'}
        />
        <div className="fa-shell">

          {/* Step header (segmented progress, like the iOS flow) */}
          {!invalidLink && step !== 'finishing' && (
            <motion.div
              className="fa-header"
              initial={{ opacity: 0, y: -14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="fa-back-slot">
                <AnimatePresence>
                  {backTarget && (
                    <motion.button
                      className="fa-back"
                      onClick={() => {
                        funnel.track(EVENTS.FUNNEL_BACK_CLICKED, {
                          from_step: step,
                          to_step: backTarget,
                        });
                        setStep(backTarget);
                      }}
                      aria-label="Back"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
              <div className="fa-segments">
                {STEP_ORDER.map((s, i) => (
                  <div key={s} className={`fa-segment ${i <= stepIndex ? 'filled' : ''}`} />
                ))}
              </div>
            </motion.div>
          )}

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={cardKey}
              className={`fa-card fa-center ${cardKey === 'meet' ? 'fa-meet' : ''} ${cardKey === 'signin' ? 'fa-card-sheet' : ''}`}
              custom={direction}
              variants={cardKey === 'signin' ? sheetStepVariants : stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {invalidLink && (
                <>
                  <motion.div variants={itemVariants} className="fa-panda-wrap">
                    <img className="fa-panda fa-float" src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
                  </motion.div>
                  <motion.h1 variants={itemVariants} className="fa-title">Hmm, this link isn't quite right</motion.h1>
                  <motion.p variants={itemVariants} className="fa-subtitle">
                    This page is part of the Pixley sign-up flow. Head to{' '}
                    <a href="https://www.pixleyai.com">pixleyai.com</a> to get started.
                  </motion.p>
                </>
              )}

              {/* The explainer slides — only for visitors who chose "See how Pixley works" */}
              {!invalidLink && EXPLAINER_BY_KEY[step] && (
                <ExplainerSlide
                  slide={EXPLAINER_BY_KEY[step]}
                  onCreate={() => {
                    funnel.trackStepCompleted(step);
                    setStep('create');
                  }}
                  onNext={() => {
                    funnel.trackStepCompleted(step);
                    setStep(EXPLAINER_STEPS[EXPLAINER_STEPS.indexOf(step) + 1]);
                  }}
                  itemVariants={itemVariants}
                />
              )}

              {/* STEP 1 — pick a ready-made friend, or use a photo */}
              {!invalidLink && step === 'create' && (
                <FunnelFriendStep
                  friends={PRESET_FRIENDS}
                  pick={pick}
                  onPickFriend={(key) => setPick({ type: 'friend', key })}
                  onPickPhoto={() => setPick({ type: 'photo' })}
                  photoPreviewUrl={photoPreviewUrl}
                  fileInputRef={fileInputRef}
                  cameraInputRef={cameraInputRef}
                  onPhotoChange={handlePhotoChange}
                  onOpenCamera={openCamera}
                  loadingFriend={loadingFriend}
                  onConfirm={confirmPick}
                  itemVariants={itemVariants}
                />
              )}

              {/* STEP 1b — generating (NOCharacterGenerationStep) */}
              {!invalidLink && step === 'generating' && (
                <>
                  <motion.div variants={itemVariants}>
                    <div className="fa-panda-glow">
                      <LoadingPandaVideo className="fa-panda-video" />
                    </div>
                  </motion.div>
                  <motion.h1 variants={itemVariants} className="fa-title">Bringing them to life…</motion.h1>
                  <motion.div variants={itemVariants} className="fa-gen-msg">
                    <AnimatePresence mode="wait">
                      <motion.p
                        key={genMsgIndex}
                        className="fa-subtitle"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        {GEN_MESSAGES[genMsgIndex]}
                      </motion.p>
                    </AnimatePresence>
                  </motion.div>
                  <motion.div variants={itemVariants} className="fa-progress-row">
                    <div className="fa-progress-track">
                      <div className="fa-progress-fill" style={{ width: `${Math.round(genProgress * 100)}%` }}>
                        <div className="fa-progress-shimmer" />
                      </div>
                    </div>
                    <span className="fa-progress-label">{Math.round(genProgress * 100)}%</span>
                  </motion.div>

                  {/* The one open-ended wait in the funnel — offer something to
                      watch rather than just the bar. Plays in an overlay, so the
                      generation request in flight here is never abandoned. */}
                  <FunnelWaitVideo
                    onPlay={(episode) =>
                      funnel.track(EVENTS.FUNNEL_WAIT_VIDEO_PLAYED, {
                        episode_id: episode?.id || null,
                        episode_name: episode?.name || null,
                        // Where in the wait they gave up on the bar
                        progress_percent: Math.round(genProgress * 100),
                      })
                    }
                  />
                </>
              )}

              {/* STEP 2 — meet the character (NOMeetPixleyStep) */}
              {!invalidLink && step === 'meet' && previewImage && (
                <>
                  {heroRevealed && <Confetti />}
                  <FunnelMeetStep
                    friend={metFriend}
                    previewImage={previewImage}
                    heroRevealed={heroRevealed}
                    onSave={() => {
                      funnel.trackStepCompleted('meet');
                      setStep('name');
                    }}
                    itemVariants={itemVariants}
                  />
                </>
              )}

              {/* STEP 3 — name (NOChildNameStep), once they've seen their character */}
              {!invalidLink && step === 'name' && (
                <FunnelNameStep
                  childName={childName}
                  onChangeName={(value) => {
                    setChildName(value);
                    sessionStorage.setItem(FUNNEL_NAME_KEY, value);
                  }}
                  onSubmit={submitName}
                  itemVariants={itemVariants}
                />
              )}

              {/* STEP 4 — sign in (NOSignInStep) */}
              {!invalidLink && step === 'signin' && (
                <FunnelSigninStep
                  childName={childName}
                  friend={metFriend}
                  previewImage={previewImage}
                  isSigningIn={isSigningIn}
                  onGoogleSignIn={handleGoogleSignIn}
                  itemVariants={itemVariants}
                />
              )}

              {/* Finishing — saving character + returning to the funnel */}
              {!invalidLink && step === 'finishing' && (
                showSuccess ? (
                  <>
                    <div className="fa-success-check"><CheckIcon size={42} /></div>
                    <h1 className="fa-title">You're all set!</h1>
                    <p className="fa-subtitle">Taking you back to finish signing up…</p>
                  </>
                ) : (
                  <>
                    <motion.div variants={itemVariants}>
                      <div className="fa-panda-glow">
                        <LoadingPandaVideo className="fa-panda-video" />
                      </div>
                    </motion.div>
                    <motion.h1 variants={itemVariants} className="fa-title">Setting up your account…</motion.h1>
                    <motion.p variants={itemVariants} className="fa-subtitle">
                      {childName.trim() ? `Saving ${childName.trim()}'s character to your account.` : 'Just a moment.'}
                    </motion.p>
                  </>
                )
              )}
            </motion.div>
          </AnimatePresence>

          {/* Sign-in's sheet carries its own wordmark, at the foot of the screen */}
          {cardKey !== 'signin' && (
            <motion.p
              className="fa-footer-note"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              PIXLEY
            </motion.p>
          )}

        </div>

        {/* In-page camera (desktop, where the capture attribute is ignored) */}
        <AnimatePresence>
          {cameraOpen && (
            <motion.div
              className="fa-camera-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div
                className="fa-camera-modal"
                initial={{ opacity: 0, scale: 0.92, y: 18 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 8 }}
                transition={{ type: 'spring', stiffness: 340, damping: 28 }}
              >
                <video ref={cameraVideoRef} className="fa-camera-video" autoPlay playsInline muted />
                <div className="fa-camera-actions">
                  <button className="fa-camera-cancel" onClick={closeCamera}>Cancel</button>
                  <button className="fa-camera-shutter" onClick={capturePhoto} aria-label="Take photo" />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Existing account — welcome back, then off to the main site */}
        <AnimatePresence>
          {showExistingAccount && (
            <motion.div
              className="fa-exist-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <motion.div
                className="fa-exist-modal"
                role="dialog"
                aria-modal="true"
                aria-label="You already have an account"
                initial={{ opacity: 0, scale: 0.82, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              >
                <span className="fa-exist-glow" aria-hidden="true" />
                <motion.img
                  className="fa-panda"
                  src="/assets/pixley-panda-nobg.png"
                  alt="Pixley Panda"
                  initial={{ scale: 0, rotate: -14 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 15, delay: 0.15 }}
                />
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="fa-eyebrow">Welcome back</div>
                  <h1 className="fa-title">You already have an account!</h1>
                  <p className="fa-subtitle">No need to sign up again — we're taking you back to Pixley.</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                >
                  <div className="fa-exist-progress" aria-hidden="true"><div /></div>
                  <button
                    className="fa-primary-btn"
                    onClick={() => window.location.replace('https://www.pixleyai.com')}
                  >
                    Take me there now <span className="fa-chevron">›</span>
                  </button>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  );
};

// iOS onboarding theme, translated from NewOnboardingKit.swift:
// bg #F5FAFF→#E8F1FF, ink #0B1A38, subtitle #6C7A8F, accent #0A6CFF,
// card stroke #E8EDF5, track #DDE6F2, rounded Fredoka titles.
const FunnelAuthPageStyles = () => (
  <style>{`
    .fa-bg {
      position: relative;
      min-height: 100vh;
      min-height: 100dvh;
      /* The night sky's own colours, under FunnelSky until its poster paints */
      background: linear-gradient(180deg, #243EA0 0%, #637BE9 50%, #BFABF1 100%);
      font-family: 'Inter', 'Fredoka', sans-serif;
      display: flex;
      padding: calc(32px + env(safe-area-inset-top, 0px))
               calc(16px + env(safe-area-inset-right, 0px))
               calc(32px + env(safe-area-inset-bottom, 0px))
               calc(16px + env(safe-area-inset-left, 0px));
      box-sizing: border-box;
    }

    /* Touch polish — no gray tap flash, no double-tap zoom delay */
    .fa-primary-btn, .fa-back,
    .fa-camera-shutter, .fa-camera-cancel {
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    }

    /* The shell spans the screen's height with the header pinned to the top,
       so the back button and progress bar sit at the same height on every
       step. Each card centres itself in the room below, the wordmark at the
       foot; the margins degrade to plain scrolling when a card is taller than
       the screen. */
    .fa-shell {
      width: 100%; max-width: 520px;
      margin: 0 auto;
      align-self: stretch;
      display: flex; flex-direction: column;
      position: relative; z-index: 1;
    }
    .fa-shell > .fa-card { margin: auto 0; }

    /* Sign-in (FunnelSigninStep): no card. The sheet rises out of the bottom
       of the screen, edge to edge on phones. */
    .fa-bg.is-sheet { padding-bottom: 0; }
    .fa-shell > .fa-card.fa-card-sheet {
      flex: 1;
      margin: 0;
      display: flex; flex-direction: column;
      padding: 0;
      background: none;
      box-shadow: none;
      border-radius: 0;
      overflow: visible;
    }
    @media (max-width: 560px) {
      .fa-card-sheet .fs-signin-sheet {
        margin-left: calc(-16px - env(safe-area-inset-left, 0px));
        margin-right: calc(-16px - env(safe-area-inset-right, 0px));
      }
    }
    @media (max-width: 480px) {
      .fa-card-sheet .fs-signin-sheet {
        margin-left: calc(-12px - env(safe-area-inset-left, 0px));
        margin-right: calc(-12px - env(safe-area-inset-right, 0px));
      }
    }

    /* Step header */
    .fa-header {
      display: flex;
      align-items: center;
      gap: 14px;
      margin: 0 4px 22px;
    }
    .fa-back-slot {
      width: 44px; height: 44px; flex-shrink: 0;
      transition: width 0.35s cubic-bezier(.22,1,.36,1);
    }
    /* No back button (first step, reveal): the progress bar stretches into its
       place, but the header keeps its height, so the bar stays level */
    .fa-back-slot:empty { width: 0; }
    .fa-back {
      width: 44px; height: 44px;
      display: flex; align-items: center; justify-content: center;
      background: #FFF;
      border: none;
      border-radius: 14px;
      color: #162642;
      cursor: pointer;
      box-shadow: 0 4px 14px rgba(40,62,128,0.10);
      transition: box-shadow 0.2s, color 0.2s;
    }
    .fa-back:hover {
      box-shadow: 0 6px 18px rgba(41,111,251,0.18);
      color: #296FFB;
    }
    .fa-segments { display: flex; gap: 8px; flex: 1; }
    .fa-segment {
      height: 6px; flex: 1; border-radius: 3px;
      background: rgba(255,255,255,0.3);
      overflow: hidden;
    }
    .fa-segment::after {
      content: '';
      display: block;
      height: 100%; width: 0;
      border-radius: 3px;
      background: #FFF;
      transition: width 0.6s cubic-bezier(.22,1,.36,1);
    }
    .fa-segment.filled::after { width: 100%; }

    /* Card. Opaque, and deliberately no backdrop-filter: that would make the
       card the containing block for position:fixed descendants, trapping the
       wait video's full-screen overlay (FunnelWaitVideo) inside it. */
    .fa-card {
      position: relative;
      background: #FFF;
      border-radius: 32px;
      box-shadow: 0 24px 60px rgba(14,24,84,0.26), 0 2px 8px rgba(14,24,84,0.10);
      padding: 34px 26px 30px;
      overflow: hidden;
    }
    .fa-center { text-align: center; }

    .fa-footer-note {
      text-align: center;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 15px; font-weight: 700;
      color: rgba(255,255,255,0.88);
      text-shadow: 0 1px 10px rgba(36,48,140,0.35);
      letter-spacing: 5px;
      margin: 22px 0 0;
    }

    /* Type — same values as the shared .fs-* screens (FunnelScreens.js), for
       the steps that are this page's own (waits, invalid link, welcome back) */
    .fa-eyebrow {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 13px; font-weight: 700;
      letter-spacing: 0.15em; text-transform: uppercase;
      color: #2869FC; margin-bottom: 12px;
    }
    .fa-title {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 32px; font-weight: 700;
      color: #162642; line-height: 1.1;
      letter-spacing: -0.02em;
      margin: 0 0 12px;
      text-wrap: balance;
    }
    .fa-subtitle {
      font-size: 16.5px; color: #6B7892; line-height: 1.5;
      margin: 0 0 22px;
      text-wrap: pretty;
    }
    .fa-subtitle a { color: #296FFB; font-weight: 600; }

    /* Panda mascot */
    .fa-panda-wrap { display: flex; justify-content: center; }
    .fa-panda {
      width: 118px; height: 118px;
      object-fit: contain;
      margin-bottom: 10px;
      filter: drop-shadow(0 10px 18px rgba(11,26,51,0.14));
    }
    .fa-float { animation: faFloat 2.6s ease-in-out infinite; }
    @keyframes faFloat {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-7px); }
    }
    .fa-panda-glow {
      width: 150px; height: 150px; margin: 0 auto 12px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(10,108,255,0.16) 0%, rgba(10,108,255,0) 70%);
      display: flex; align-items: center; justify-content: center;
      animation: faPulse 1.9s ease-in-out infinite;
    }
    @keyframes faPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    .fa-panda-video { width: 140px; height: 140px; object-fit: contain; }

    /* Buttons — primary matches .fs-cta */
    .fa-primary-btn {
      position: relative;
      width: 100%;
      min-height: 58px;
      display: flex; align-items: center; justify-content: center; gap: 10px;
      padding: 14px 22px;
      background: linear-gradient(90deg, #296FFB 0%, #3E8DFD 100%);
      color: #FFF;
      font-size: 20px; font-weight: 700;
      font-family: 'Fredoka', 'Inter', sans-serif;
      border: none; border-radius: 20px;
      cursor: pointer;
      box-shadow: 0 12px 26px rgba(41,111,251,0.26), inset 0 1px 0 rgba(255,255,255,0.22);
      transition: transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms, opacity 0.2s;
    }
    .fa-primary-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 16px 32px rgba(41,111,251,0.34), inset 0 1px 0 rgba(255,255,255,0.22);
    }
    .fa-primary-btn:active:not(:disabled) { transform: scale(0.98); }
    .fa-primary-btn:disabled { opacity: 0.45; cursor: default; box-shadow: none; }
    .fa-chevron { font-size: 22px; line-height: 1; transition: transform 0.2s ease; }
    .fa-primary-btn:hover:not(:disabled) .fa-chevron { transform: translateX(3px); }

    .fa-primary-btn:focus-visible, .fa-back:focus-visible {
      outline: 3px solid rgba(41,111,251,0.4);
      outline-offset: 2px;
    }

    /* In-page camera modal (desktop fallback for the capture attribute) */
    .fa-camera-overlay {
      position: fixed; inset: 0; z-index: 60;
      background: rgba(11,26,51,0.72);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
    }
    .fa-camera-modal {
      width: min(560px, 100%);
      background: #FFF;
      border-radius: 24px;
      padding: 16px;
      box-shadow: 0 24px 60px rgba(11,26,51,0.35);
    }
    .fa-camera-video {
      width: 100%;
      border-radius: 16px;
      transform: scaleX(-1); /* mirror the live preview like a selfie cam */
      background: #0B1A38;
    }
    .fa-camera-actions {
      position: relative;
      display: flex; align-items: center; justify-content: center;
      padding-top: 14px;
    }
    .fa-camera-cancel {
      position: absolute; left: 8px;
      background: none; border: none;
      font-family: 'Inter', sans-serif;
      font-size: 15px; font-weight: 600; color: #6C7A8F;
      cursor: pointer;
      transition: color 0.2s;
    }
    .fa-camera-cancel:hover { color: #0B1A38; }
    .fa-camera-shutter {
      width: 58px; height: 58px;
      border-radius: 50%;
      background: #0A6CFF;
      border: 4px solid #FFF;
      box-shadow: 0 0 0 3px #0A6CFF;
      cursor: pointer;
      transition: transform 0.15s cubic-bezier(.2,.8,.2,1);
    }
    .fa-camera-shutter:hover { transform: scale(1.08); }
    .fa-camera-shutter:active { transform: scale(0.92); }

    /* Existing-account modal */
    .fa-exist-overlay {
      position: fixed; inset: 0; z-index: 70;
      background: rgba(11,26,51,0.55);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
    }
    .fa-exist-modal {
      position: relative;
      width: min(430px, 100%);
      background: #FFF;
      border-radius: 28px;
      padding: 36px 28px 28px;
      text-align: center;
      overflow: hidden;
      box-shadow: 0 30px 80px rgba(11,26,51,0.35);
    }
    .fa-exist-glow {
      position: absolute; top: -70px; left: 50%;
      width: 260px; height: 260px;
      transform: translateX(-50%);
      border-radius: 50%;
      background: radial-gradient(circle, rgba(10,108,255,0.14) 0%, rgba(10,108,255,0) 70%);
      pointer-events: none;
    }
    .fa-exist-modal .fa-panda { display: block; margin: 0 auto 10px; }
    .fa-exist-modal .fa-title { font-size: 26px; }
    .fa-exist-modal .fa-subtitle { margin-bottom: 20px; }
    .fa-exist-progress {
      height: 6px;
      background: #E8EDF5;
      border-radius: 3px;
      overflow: hidden;
      margin: 0 0 18px;
    }
    .fa-exist-progress div {
      height: 100%;
      width: 0;
      border-radius: 3px;
      background: linear-gradient(90deg, #0A6CFF, #7C5CFF);
      animation: faExistCountdown 3.4s linear forwards;
    }
    @keyframes faExistCountdown { to { width: 100%; } }

    /* Progress bar (NOProgressBar with shimmer) */
    .fa-gen-msg {
      min-height: 50px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 18px;
    }
    .fa-gen-msg .fa-subtitle { margin: 0; }
    .fa-progress-row { display: flex; align-items: center; gap: 12px; }
    .fa-progress-track {
      flex: 1; height: 12px;
      background: #DDE6F2;
      border-radius: 6px;
      overflow: hidden;
    }
    .fa-progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #296FFB, #3E8DFD);
      border-radius: 6px;
      position: relative;
      overflow: hidden;
      transition: width 0.5s ease-in-out;
    }
    .fa-progress-shimmer {
      position: absolute; inset: 0;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%);
      animation: faShimmer 1.4s linear infinite;
    }
    @keyframes faShimmer {
      from { transform: translateX(-100%); }
      to { transform: translateX(100%); }
    }
    .fa-progress-label {
      font-size: 15px; font-weight: 700; color: #0B1A38;
      font-variant-numeric: tabular-nums;
      min-width: 44px; text-align: right;
    }

    /* Meet step — the confetti falls past the card edge */
    .fa-meet { overflow: visible; }

    /* Confetti */
    .fa-confetti {
      position: absolute; inset: 0;
      pointer-events: none;
      overflow: hidden;
      border-radius: 32px;
      z-index: 1;
    }
    .fa-confetti span {
      position: absolute;
      top: -20px;
      width: 11px; height: 11px;
      border-radius: 2px;
      animation-name: faConfettiFall;
      animation-timing-function: ease-in;
      animation-fill-mode: forwards;
    }
    @keyframes faConfettiFall {
      to {
        transform: translate(var(--drift), 640px) rotate(var(--spin));
        opacity: 0;
      }
    }

    /* Success */
    .fa-success-check {
      position: relative;
      width: 80px; height: 80px;
      margin: 0 auto 18px;
      border-radius: 50%;
      background: linear-gradient(135deg, #3EDC8F, #20A96A);
      color: #FFF;
      font-size: 34px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 10px 26px rgba(32,169,106,0.35);
      animation: faSuccessPop 0.6s ease-out;
    }
    .fa-success-check::after {
      content: '';
      position: absolute; inset: 0;
      border-radius: 50%;
      border: 3px solid rgba(62,220,143,0.5);
      animation: faRipple 1.4s ease-out infinite;
    }
    @keyframes faSuccessPop {
      0% { transform: scale(0); opacity: 0; }
      50% { transform: scale(1.2); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes faRipple {
      from { transform: scale(1); opacity: 1; }
      to { transform: scale(1.7); opacity: 0; }
    }

    /* Mobile */
    @media (max-width: 480px) {
      .fa-bg {
        padding: calc(18px + env(safe-area-inset-top, 0px))
                 calc(12px + env(safe-area-inset-right, 0px))
                 calc(20px + env(safe-area-inset-bottom, 0px))
                 calc(12px + env(safe-area-inset-left, 0px));
      }
      .fa-card { padding: 26px 18px 24px; border-radius: 30px; }
      .fa-confetti { border-radius: 30px; }
      .fa-title { font-size: 29px; }
      .fa-subtitle { font-size: 16px; }
      .fa-panda { width: 100px; height: 100px; }
      .fa-header { gap: 12px; margin-bottom: 18px; }

      .fa-primary-btn { font-size: 19px; }
      .fa-footer-note { font-size: 13px; margin-top: 16px; }
    }

    /* Small phones (iPhone SE, compact Androids) */
    @media (max-width: 374px) {
      .fa-card { padding: 22px 14px 20px; }
      .fa-title { font-size: 26px; }
      .fa-panda { width: 88px; height: 88px; }
    }

    /* Short viewports (landscape phones, small screens with keyboard up):
       compress the tall decorative pieces so the card fits without scroll */
    @media (max-height: 640px) {
      .fa-bg { padding-top: 14px; padding-bottom: 14px; }
      .fa-header { margin-bottom: 12px; }
      .fa-card { padding: 22px 20px 20px; }
      .fa-title { font-size: 26px; }
      .fa-subtitle { margin-bottom: 16px; }
      .fa-panda { width: 78px; height: 78px; margin-bottom: 6px; }
      .fa-panda-glow { width: 112px; height: 112px; margin-bottom: 8px; }
      .fa-panda-video { width: 104px; height: 104px; }
      .fa-gen-msg { min-height: 42px; margin-bottom: 14px; }
      .fa-footer-note { display: none; }
    }

    /* Touch devices — bigger camera shutter for thumbs */
    @media (pointer: coarse) {
      .fa-camera-shutter { width: 66px; height: 66px; }
    }

    @media (prefers-reduced-motion: reduce) {
      .fa-float, .fa-panda-glow, .fa-progress-shimmer,
      .fa-success-check, .fa-success-check::after { animation: none; }
      .fa-primary-btn:hover:not(:disabled) { transform: none; }
      .fa-confetti { display: none; }
    }
  `}</style>
);

export default FunnelAuthPage;
